import { STATUS_METADATA } from '~/types/orders.ts';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatOrderStatus } from '~/utils';
import { CheckCircle, CircleXIcon, Clock } from 'lucide-react';
import type { OrderStatus, TradePerspective } from '~/graphql/graphql';

export function OrderStatusCard({
  orderStatus,
  deliveredDate,
  viewerPerspective,
}: {
  orderStatus: OrderStatus;
  deliveredDate?: string | null;
  viewerPerspective: TradePerspective;
}) {
  const { t } = useTranslation('order');

  const tradeRole: 'BUYER' | 'SELLER' =
    viewerPerspective === 'BUYING' ? 'BUYER' : 'SELLER';
  const trackingMessage = STATUS_METADATA[orderStatus].message[tradeRole];
  const deliveredDateFormatted = deliveredDate
    ? formatDateTime(deliveredDate)
    : undefined;

  let statusWrapperClassNames = 'bg-warning-50 border-warning-200';
  let statusTitleClassName = 'text-warning-900';
  let statusMessageClassName = 'text-warning-700';
  let icon = <Clock size={18} className="text-warning-700" />;

  if (orderStatus === 'DELIVERED') {
    statusWrapperClassNames = 'bg-success-50 border-success-200';
    statusTitleClassName = 'text-success-900';
    statusMessageClassName = 'text-success-700';
    icon = <CheckCircle size={18} className="text-success-700" />;
  }

  if (orderStatus === 'CANCELLED') {
    statusWrapperClassNames = 'bg-error-100 border-error-200';
    statusTitleClassName = 'text-error-800';
    statusMessageClassName = 'text-error-700';
    icon = <CircleXIcon size={18} className="text-error-700" />;
  }

  return (
    <div className={`p-4 rounded-lg border ${statusWrapperClassNames}`}>
      <div className="flex items-start gap-2">
        {icon}
        <div>
          <p className={`text-sm font-medium mb-1 ${statusTitleClassName}`}>
            {t('order.status')}: {formatOrderStatus(orderStatus, tradeRole)}
          </p>
          <p className={`text-xs ${statusMessageClassName}`}>
            {t(trackingMessage, { deliveredDate: deliveredDateFormatted })}
          </p>
        </div>
      </div>
    </div>
  );
}
